import React, { useState, useEffect } from 'react';
import { useNotifier } from '../ui/Notifier';
import { cotacaoService } from '../../services/cotacaoService';
import type { Cotacao } from '../../types/cotacao';

interface EditCotacaoModalProps {
  cotacao: Cotacao | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

// Parser de moeda
const parseCurrency = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
};

// Formatar moeda para exibição
const formatCurrency = (value: number | string | undefined): string => {
  if (!value) return '';
  const num = typeof value === 'string' ? parseCurrency(value) : value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Formatar percentual
const formatPercentual = (value: number | string | undefined): string => {
  if (!value) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const EditCotacaoModal: React.FC<EditCotacaoModalProps> = ({
  cotacao,
  isOpen,
  onClose,
  onSave,
}) => {
  const notifier = useNotifier();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    seguradora: '',
    plano: '',
    tipoPlano: '',
    premioTotal: '',
    percentualComis: '',
    valorComissao: '',
    premioLiquido: '',
    informacoes: '',
    selecionada: false,
  });

  // Preencher o formulário quando a cotação é carregada
  useEffect(() => {
    if (cotacao) {
      setFormData({
        seguradora: cotacao.seguradora || '',
        plano: cotacao.plano || '',
        tipoPlano: cotacao.tipoPlano || '',
        premioTotal: formatCurrency(cotacao.premioTotal),
        percentualComis: formatPercentual(cotacao.percentualComis),
        valorComissao: formatCurrency(cotacao.valorComissao),
        premioLiquido: formatCurrency(cotacao.premioLiquido),
        informacoes: cotacao.informacoes || '',
        selecionada: cotacao.selecionada || false,
      });
    }
  }, [cotacao]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = formatCurrency(parseFloat(numericValue) / 100);
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handlePercentualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = formatPercentual(parseFloat(numericValue) / 100);
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!cotacao?.id) {
      notifier.show({ message: 'ID da cotação não encontrado', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const updateData: Partial<Cotacao> = {
        seguradora: formData.seguradora,
        plano: formData.plano || undefined,
        tipoPlano: formData.tipoPlano || undefined,
        premioTotal: formData.premioTotal ? parseCurrency(formData.premioTotal) : undefined,
        percentualComis: formData.percentualComis ? parseCurrency(formData.percentualComis) : undefined,
        valorComissao: formData.valorComissao ? parseCurrency(formData.valorComissao) : undefined,
        premioLiquido: formData.premioLiquido ? parseCurrency(formData.premioLiquido) : undefined,
        informacoes: formData.informacoes || undefined,
        selecionada: formData.selecionada,
      };

      const response = await cotacaoService.update(cotacao.id, updateData);

      if (response.success) {
        notifier.show({ message: 'Cotação atualizada com sucesso!', severity: 'success' });
        onSave();
        onClose();
      } else {
        notifier.show({ message: response.message || 'Erro ao atualizar cotação', severity: 'error' });
      }
    } catch (error: any) {
      notifier.show({ 
        message: error.message || 'Erro ao atualizar cotação', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(61, 43, 86, 0.5)' }}>
      <div className="bg-white w-[95vw] max-w-[1000px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header com dados principais */}
        <div className="bg-white px-4 py-3" style={{ borderBottom: '2px solid #C3B4DE' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#F4EFFA' }}>
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Editar Cotação</h3>
                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-0.5">
                  {formData.seguradora && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {formData.seguradora}
                    </span>
                  )}
                  {formData.plano && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {formData.plano}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Seguradora */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Seguradora <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="seguradora"
                value={formData.seguradora}
                onChange={handleInputChange}
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                required
              />
            </div>

            {/* Plano */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Plano
              </label>
              <input
                type="text"
                name="plano"
                value={formData.plano}
                onChange={handleInputChange}
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Tipo de Plano */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Tipo de Plano
              </label>
              <input
                type="text"
                name="tipoPlano"
                value={formData.tipoPlano}
                onChange={handleInputChange}
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Prêmio Total */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Prêmio Total (R$)
              </label>
              <input
                type="text"
                name="premioTotal"
                value={formData.premioTotal}
                onChange={handleCurrencyChange}
                placeholder="0,00"
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Percentual de Comissão */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Percentual de Comissão (%)
              </label>
              <input
                type="text"
                name="percentualComis"
                value={formData.percentualComis}
                onChange={handlePercentualChange}
                placeholder="0,00"
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Valor de Comissão */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Valor de Comissão (R$)
              </label>
              <input
                type="text"
                name="valorComissao"
                value={formData.valorComissao}
                onChange={handleCurrencyChange}
                placeholder="0,00"
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Prêmio Líquido */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Prêmio Líquido (R$)
              </label>
              <input
                type="text"
                name="premioLiquido"
                value={formData.premioLiquido}
                onChange={handleCurrencyChange}
                placeholder="0,00"
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
              />
            </div>

            {/* Selecionada */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="selecionada"
                checked={formData.selecionada}
                onChange={handleInputChange}
                className="w-4 h-4 rounded focus:ring-1"
                style={{ color: '#7A61B2' }}
              />
              <label className="ml-2 text-xs font-medium" style={{ color: '#3d2b56' }}>
                Cotação Selecionada
              </label>
            </div>

            {/* Informações - ocupa 2 colunas */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Informações Adicionais
              </label>
              <textarea
                name="informacoes"
                value={formData.informacoes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-1 text-xs resize-none"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212' }}
                placeholder="Digite informações adicionais sobre a cotação..."
              />
            </div>
          </div>
        </div>

        {/* Footer com botões */}
        <div className="px-6 py-3 flex justify-end gap-2" style={{ borderTop: '2px solid #C3B4DE', backgroundColor: '#F9F8FC' }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-medium rounded-md transition-colors"
            style={{ backgroundColor: '#E5E5E5', color: '#3d2b56' }}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-1.5 text-xs font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#7A61B2' }}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </form>
    </div>
  </div>
  );
};
